import { useState } from 'react';

export default function MandatoryDiscretionarySection() {
  const [activeTab, setActiveTab] = useState('mandatory');

  return (
    <section className="section-wrap bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading">
            Mandatory vs. Discretionary Grants
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding the difference between mandatory and discretionary grants to maximise your funding opportunities
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('mandatory')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                activeTab === 'mandatory'
                  ? 'bg-[#2D7F3E] text-white'
                  : 'text-gray-600 hover:text-[#2D7F3E]'
              }`}
            >
              Mandatory Grants (20%)
            </button>
            <button
              onClick={() => setActiveTab('discretionary')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                activeTab === 'discretionary'
                  ? 'bg-[#2D7F3E] text-white'
                  : 'text-gray-600 hover:text-[#2D7F3E]'
              }`}
            >
              Discretionary Grants (49.5%)
            </button>
          </div>

          <div className="p-8">
            {activeTab === 'mandatory' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Mandatory Grants
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Automatic entitlement to 20% of your SDL contribution through compliant WSP and ATR submissions.
                  </p>
                  
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Key Features:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Guaranteed funding for compliant companies</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Based on WSP and ATR submissions</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">No additional application required</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Covers PIVOTAL and non-PIVOTAL training</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Eligibility Requirements:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Submit WSP by April 30</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Submit ATR by April 30</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Maintain SETA registration</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Meet minimum training spend requirements</span>
                    </li>
                  </ul>

                  <div className="mt-6 p-4 bg-[#2D7F3E] text-white rounded-lg">
                    <p className="font-semibold text-center">Average Recovery: R150,000 - R2.5M</p>
                    <p className="text-sm text-green-100 text-center">Depending on company size and SDL contribution</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'discretionary' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Discretionary Grants
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Additional funding opportunities through competitive applications for specific training projects and initiatives.
                  </p>
                  
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Grant Types:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Large employer grants (R500k+)</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Small employer grants (up to R500k)</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Sector-specific funding</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-[#2D7F3E] text-xl mr-3 mt-1"></i>
                        <span className="text-gray-600">Special project funding</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Application Requirements:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Detailed project proposal</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Budget breakdown and justification</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Training provider accreditation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#2D7F3E] mt-1 mr-2"></i>
                      <span className="text-gray-600">Learner selection criteria</span>
                    </li>
                  </ul>

                  <div className="mt-6 p-4 bg-amber-500 text-white rounded-lg">
                    <p className="font-semibold text-center">Potential Additional Funding</p>
                    <p className="text-sm text-amber-100 text-center">Up to 49.5% of SDL through successful applications</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#2D7F3E] to-green-600 text-white p-8 rounded-xl text-center">
          <h3 className="text-3xl font-bold mb-4">Maximise Your Savings</h3>
          <p className="text-xl text-green-100 mb-6">
            Combined mandatory and discretionary grants can recover up to 69.5% of your SDL
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 p-4 rounded-lg">
              <div className="text-2xl font-bold">20%</div>
              <div className="text-green-100">Mandatory Grants</div>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <div className="text-2xl font-bold">49.5%</div>
              <div className="text-green-100">Discretionary Grants</div>
            </div>
            <div className="bg-white/20 p-4 rounded-lg border-2 border-white/30">
              <div className="text-3xl font-bold">69.5%</div>
              <div className="text-white font-semibold">Total Potential Recovery</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}